/**
 * AiGenerateWizard — the 5-step modal: setup → prompt → paste → review →
 * done, with step dots and a slide between steps. Opened from the library
 * (campaign pickable) or from a campaign drawer (context locked, optional
 * load-into-draft).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { FlowOptimizerLibraryProvider } from '@trafficpulse/shared';

import ModalCard from '@/components/ui/layout/ModalCard';
import { cn } from '@/lib/utils';

import type { AssembledLibraryPayload } from '../validate/assemble-accepted';
import DoneStep from './steps/DoneStep';
import PasteStep from './steps/PasteStep';
import PromptStep from './steps/PromptStep';
import ReviewStep from './steps/ReviewStep';
import SetupStep from './steps/SetupStep';
import { useAiWizard, type WizardStep } from './use-ai-wizard';
import { useCreateAccepted } from './use-create-accepted';

const STEPS: WizardStep[] = ['setup', 'prompt', 'paste', 'review', 'done'];

export interface AiGenerateWizardProps {
  open: boolean;
  onClose: () => void;
  provider: FlowOptimizerLibraryProvider;
  campaignId: string;
  /** Locked campaign context (drawer entry) + optional draft loader. */
  campaignLocked?: boolean;
  draftApply?: (assembled: AssembledLibraryPayload) => void;
}

const AiGenerateWizard: FC<AiGenerateWizardProps> = ({
  open,
  onClose,
  provider,
  campaignId,
  campaignLocked = false,
  draftApply,
}) => {
  const wizard = useAiWizard({ provider, campaignId });
  const create = useCreateAccepted();
  const [draftApplied, setDraftApplied] = useState(false);

  const handleCreate = (input: { name: string; applyToDraft: boolean }) => {
    create.mutate(
      { items: wizard.items, name: input.name, target: wizard.options.target },
      {
        onSuccess: (result) => {
          if (input.applyToDraft && draftApply) {
            draftApply(result.assembled);
            setDraftApplied(true);
          }
          wizard.setStep('done');
        },
      },
    );
  };

  return (
    <ModalCard
      open={open}
      title="Generate with AI"
      subtitle="Build a prompt from live campaign data, run it in your AI of choice, paste the JSON back."
      widthClass="max-w-3xl"
      zClass="z-[130]"
      busy={create.isPending}
      onClose={onClose}
    >
      <div className="space-y-3">
        <StepDots current={wizard.step} />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={wizard.step}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.16 }}
          >
            {wizard.step === 'setup' ? (
              <SetupStep wizard={wizard} campaignLocked={campaignLocked} />
            ) : wizard.step === 'prompt' ? (
              <PromptStep wizard={wizard} />
            ) : wizard.step === 'paste' ? (
              <PasteStep wizard={wizard} />
            ) : wizard.step === 'review' ? (
              <ReviewStep
                wizard={wizard}
                canApplyToDraft={Boolean(draftApply)}
                busy={create.isPending}
                onCreate={handleCreate}
              />
            ) : create.data ? (
              <DoneStep
                result={create.data}
                draftApplied={draftApplied}
                onClose={onClose}
              />
            ) : null}
          </motion.div>
        </AnimatePresence>
      </div>
    </ModalCard>
  );
};

const LABELS: Record<WizardStep, string> = {
  setup: 'Setup',
  prompt: 'Prompt',
  paste: 'Paste',
  review: 'Review',
  done: 'Done',
};

const StepDots: FC<{ current: WizardStep }> = ({ current }) => {
  const activeIndex = STEPS.indexOf(current);
  return (
    <div className="flex items-center gap-1.5">
      {STEPS.map((step, index) => (
        <span key={step} className="flex items-center gap-1.5">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              index <= activeIndex ? 'bg-primary' : 'bg-border',
            )}
            aria-hidden
          />
          <span
            className={cn(
              'text-[10px] font-medium uppercase tracking-wide',
              index === activeIndex ? 'text-foreground' : 'text-muted-foreground/60',
            )}
          >
            {LABELS[step]}
          </span>
        </span>
      ))}
    </div>
  );
};

export default AiGenerateWizard;
